/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  BankOutlined,
  CalendarOutlined,
  HistoryOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import {
  joinWithDot,
  sinceLabel,
  type TeamHistoryCopy,
  type TeamHistoryView,
} from '@sportninja/common/features/registration/teamHistory'
import Text from 'src/components/ts-components/Text'
import HistoryMetaItem from './HistoryMetaItem'
import OfficialAvatarStack from './OfficialAvatarStack'
import TeamLogo from './TeamLogo'

interface Props {
  team: { name: string; image?: unknown }
  history: TeamHistoryView
  copy: TeamHistoryCopy
}

/**
 * A team option in the team picker, with its history: competitions
 * played, last roster size, first season, where it last played, and who ran it.
 * A team that never played says so, and falls back to its organization.
 */
const TeamOptionLabel = ({ team, history, copy }: Props) => (
  <div css={rowStyle}>
    <TeamLogo team={team} size={24} />
    <div css={bodyStyle}>
      <Text
        variant='paragraphSmall'
        weight='medium'
        color={colors.WHITE}
        style={truncate}
      >
        {team.name}
      </Text>

      {history.hasPlayed ? (
        <>
          <div css={metaRowStyle}>
            <HistoryMetaItem
              icon={<TrophyOutlined />}
              label={history.competitionCountLabel}
            />
            {history.playerCountLabel ? (
              <HistoryMetaItem
                icon={<TeamOutlined />}
                label={history.playerCountLabel}
              />
            ) : null}
            {history.firstPlayedLabel ? (
              <HistoryMetaItem
                icon={<CalendarOutlined />}
                label={sinceLabel(history.firstPlayedLabel)}
              />
            ) : null}
          </div>
          {history.lastCompetition ? (
            <HistoryMetaItem
              muted
              icon={<HistoryOutlined />}
              label={joinWithDot(
                history.lastCompetition.name,
                history.lastCompetition.organizationName
              )}
            />
          ) : null}
        </>
      ) : (
        <>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {copy.noHistory}
          </Text>
          {history.organizationName ? (
            <HistoryMetaItem
              muted
              icon={<BankOutlined />}
              label={history.organizationName}
            />
          ) : null}
        </>
      )}
    </div>

    {history.officials.length > 0 ? (
      <OfficialAvatarStack
        officials={history.officials}
        treasurerLabel={copy.treasurer}
      />
    ) : null}
  </div>
)

export default TeamOptionLabel

const truncate = `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

/* Top-aligned so the logo sits level with the team name, not the middle of
   the two or three lines beside it. Logo size and gap match the identity
   dropdown's person rows, so both pickers read alike. The logo is 24px against
   a ~20px name line, so it drops 2px to sit on the name, not above it. */
const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  min-width: 0;

  > img {
    margin-top: 2px;
  }
`

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
`

/* The stats share one line; the last one gives way first on a narrow popup. */
const metaRowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
`
